import React from 'react';
import API from '../../api/api';
import { ClipPrompt } from '../../api/structs';
import { ClipStyleSelector } from '../selectors';
import { Button, Input } from '../ui';
import Modal from './Modal';
import MetadataFieldsSection from './edit-clip-prompt/MetadataFieldsSection';
import { normalizeMetadataForSubmit } from './edit-clip-prompt/utils';
import { useEditClipPromptState } from './edit-clip-prompt/useEditClipPromptState';
import { useToast } from '../../hooks/useToast';
import { builtinGenericCarouselBrandPresets } from '../../remotion/generic-carousel/brandPresets';
import { applyBuiltinCarouselBrandPresetToMetadata } from '../clips/sections/carouselStylePreset';

interface EditClipPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  clip: ClipPrompt;
  onSave: () => void;
}

const EditClipPromptModal: React.FC<EditClipPromptModalProps> = ({ isOpen, onClose, clip, onSave }) => {
  const toast = useToast();
  const {
    name,
    setName,
    style,
    setStyle,
    metadata,
    setMetadata,
    styles,
    styleSchema,
    metadataFields,
    isLoading,
    setIsLoading,
    isLoadingStyles,
    isLoadingSchema,
    schemaError,
    isLoadingMedia,
    availableMedia,
    musicMediaId,
    handleMetadataChange,
  } = useEditClipPromptState({ isOpen, clip });

  const clipStyle = clip.style?.style || 'standard';
  const isGenericCarousel = style === 'genericCarousel';
  const selectedBuiltinPresetId = builtinGenericCarouselBrandPresets.some(
    (preset) => preset.id === metadata.stylePresetId
  )
    ? String(metadata.stylePresetId)
    : '';

  const handleBuiltinPresetChange = (presetId: string) => {
    const preset = builtinGenericCarouselBrandPresets.find((item) => item.id === presetId);
    if (!preset) return;
    setMetadata((current) => applyBuiltinCarouselBrandPresetToMetadata(current, preset));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      await API.editClipPrompt(clip.id, {
        name,
        clipStyle: style,
        // The clip's music binding has to be handed to the normalizer: the write
        // replaces the whole metadata map, and without it every save of this
        // modal silently dropped `music_media_id` and unbound the clip's music.
        metadata: normalizeMetadataForSubmit(metadata, musicMediaId),
      });
      onSave();
      onClose();
    } catch (error: any) {
      toast({ text: `Failed: ${error.message}`, level: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Clip Settings — ${clipStyle}`} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-wide text-zinc-400 mb-1">Name</label>
          <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Clip name..." />
        </div>

        <ClipStyleSelector value={style} onChange={setStyle} styles={styles} isLoading={isLoadingStyles} />

        {schemaError && (
          <div className="text-sm text-zinc-200 bg-black/50 border border-white/10 rounded p-2">{schemaError}</div>
        )}
        {isLoadingSchema && <p className="text-sm text-slate-400">Loading style schema...</p>}

        {isGenericCarousel && (
          <div className="space-y-3 pt-3 border-t border-white/10">
            <div>
              <label className="block text-xs uppercase tracking-wide text-zinc-400 mb-1">Visual Preset</label>
              <select
                value={selectedBuiltinPresetId}
                onChange={(event) => handleBuiltinPresetChange(event.target.value)}
                className="w-full select"
              >
                <option value="">Choose a built-in visual preset...</option>
                {builtinGenericCarouselBrandPresets.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.name}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-zinc-500">
                Use this to apply presets like Neon Hook Poster. The technical stylePreset fields are managed automatically.
              </p>
            </div>
          </div>
        )}

        <MetadataFieldsSection
          style={style}
          styleSchema={styleSchema}
          metadata={metadata}
          metadataFields={metadataFields}
          availableMedia={availableMedia}
          isLoadingMedia={isLoadingMedia}
          onMetadataChange={handleMetadataChange}
        />

        <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
          <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" type="submit" loading={isLoading}>Save</Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditClipPromptModal;
